import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'

import { HostFamilyLecCommentGuest } from '../../../../../lib/types/models/host-family-lec-comment/host-family-lec-comment'
import { HostFamilyLecCommentInput } from '../../../../../lib/types/models/host-family-lec-comment/host-family-lec-input'
import { BaseFormProps } from '../../../../../lib/types/utils'
import { getSubmitAction } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../../utils/constants/form.constants'
import ControlledSwitch from '../../../../elements/controlled-switch'
import DateChip from '../../../../elements/date-chip'
import { Form, FormControl, FormField, FormItem } from '../../../../elements/forms/form'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../../../elements/validation-buttons'
import {
  formatHostFamilyLecCommentGuest,
  getHostFamilyLecCommentGuestKey,
  HOST_FAMILY_LEC_COMMENT_SCHEMA
} from './utils'

type Props = BaseFormProps<HostFamilyLecCommentInput, true> & {
  guests: HostFamilyLecCommentGuest[]
}

const HostFamilyLecCommentForm = ({ defaultValues, guests, ...validationButtonProps }: Props) => {
  const form = useForm<HostFamilyLecCommentInput>({
    resolver: zodResolver(HOST_FAMILY_LEC_COMMENT_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const guestOptions = guests.map((guest) => ({
    label: formatHostFamilyLecCommentGuest(guest),
    value: getHostFamilyLecCommentGuestKey(guest)
  }))

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="px-4 flex flex-col gap-3"
      >
        <div className="pt-7">
          <DateChip date={defaultValues?.updatedAt ?? new Date()} label={'Date'} />
        </div>
        <FormField
          control={form.control}
          name="guests"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledMultiSelectInput
                  {...field}
                  label="Jeune(s) / encadrant(s) concerné(s)"
                  variant="primary"
                  searchable
                  options={guestOptions}
                  selectedOptions={guestOptions.filter((option) =>
                    field.value?.includes(option.value)
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} type="textarea" label="Votre commentaire..." />
              </FormControl>
            </FormItem>
          )}
        />
        <div>
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            Visibilité :
          </Typography>
          <div className="h-2" />
          <FormField
            control={form.control}
            name="isVisibleForTheYear"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <div className="flex gap-16">
                      <ControlledRadioInput
                        {...field}
                        label={'Indéfiniment'}
                        checked={field.value === false}
                        onCheck={() => field.onChange(false)}
                      />
                      <ControlledRadioInput
                        {...field}
                        label={'Année en cours'}
                        checked={field.value === true}
                        onCheck={() => field.onChange(true)}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>
        <FormField
          control={form.control}
          name="sharedToOl"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSwitch {...field} label="Partagé avec OL" variant="tag" />
              </FormControl>
            </FormItem>
          )}
        />

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default HostFamilyLecCommentForm
